// components/SkeletonComponents/InviteUserFormSkeleton.jsx
import React from 'react';
import { Mail, Building, MapPin, Camera } from 'lucide-react';

const InviteUserFormSkeleton = () => {
  return (
    <div className="animate-fadeIn">
      <div className="">
        {/* Header Skeleton */}
        <div className="bg-gradient-to-r from-red-600 to-red-600 px-6 py-2">
          <div className="flex justify-between items-center">
            <div className="flex-1">
              <div className="h-8 w-64 bg-white/20 rounded-lg animate-shimmer"></div>
              <div className="h-4 w-96 bg-white/10 rounded mt-2 animate-shimmer animation-delay-200"></div>
            </div>
            <div className="w-6 h-6 bg-white/20 rounded animate-pulse"></div>
          </div>
        </div>

        {/* Form Content */}
        <div className="space-y-6 p-4">
          {/* Basic Information Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 relative overflow-hidden">
            {/* Animated gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-red-50/30 to-transparent animate-slide"></div>
            
            <div className="flex items-center gap-2 mb-6">
              <div className="bg-gradient-to-br from-red-100 to-pink-100 p-2 rounded-lg animate-pulse">
                <Mail className="h-5 w-5 text-red-300" />
              </div>
              <div className="h-6 w-40 bg-gray-200 rounded-md animate-shimmer"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Form Fields */}
              {[1, 2, 3, 4, 5, 6].map((index) => (
                <div key={index} className="space-y-2">
                  <div className="h-4 w-24 bg-gray-200 rounded animate-shimmer" 
                       style={{ animationDelay: `${index * 100}ms` }}></div>
                  <div className="h-10 w-full bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg animate-shimmer"
                       style={{ animationDelay: `${index * 100 + 50}ms` }}></div>
                </div>
              ))}
            </div>

            {/* Profile Picture Section */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <div className="h-4 w-48 bg-gray-200 rounded mb-4 animate-shimmer"></div>
              
              <div className="flex justify-center">
                <div className="relative">
                  <div className="w-32 h-32 rounded-full bg-gradient-to-br from-red-100 via-pink-100 to-red-100 animate-spin-slow">
                    <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
                      <Camera className="h-10 w-10 text-red-200 animate-pulse" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                <div className="h-10 w-32 bg-gradient-to-r from-red-100 to-pink-100 rounded-lg animate-pulse"></div>
                <div className="h-10 w-32 bg-gradient-to-r from-red-500 to-red-500 rounded-lg animate-pulse opacity-50"></div>
              </div>
            </div>
          </div>

          {/* Organization Details Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-50/20 to-transparent animate-slide animation-delay-500"></div>
            
            <div className="flex items-center gap-2 mb-6">
              <div className="bg-gradient-to-br from-blue-100 to-indigo-100 p-2 rounded-lg animate-pulse">
                <Building className="h-5 w-5 text-blue-300" />
              </div>
              <div className="h-6 w-48 bg-gray-200 rounded-md animate-shimmer"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((index) => (
                <div key={index} className="space-y-2">
                  <div className="h-4 w-20 bg-gray-200 rounded animate-shimmer"
                       style={{ animationDelay: `${index * 150}ms` }}></div>
                  <div className="h-10 w-full bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg animate-shimmer"
                       style={{ animationDelay: `${index * 150 + 50}ms` }}></div>
                </div>
              ))}
            </div>
          </div>

          {/* Address Information Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-50/20 to-transparent animate-slide animation-delay-1000"></div>
            
            <div className="flex items-center gap-2 mb-6">
              <div className="bg-gradient-to-br from-green-100 to-emerald-100 p-2 rounded-lg animate-pulse">
                <MapPin className="h-5 w-5 text-green-300" />
              </div>
              <div className="h-6 w-44 bg-gray-200 rounded-md animate-shimmer"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Street - Full width */}
              <div className="md:col-span-2 space-y-2">
                <div className="h-4 w-28 bg-gray-200 rounded animate-shimmer"></div>
                <div className="h-10 w-full bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg animate-shimmer animation-delay-100"></div>
              </div>
              
              {/* Other address fields */}
              {[1, 2, 3, 4].map((index) => (
                <div key={index} className="space-y-2">
                  <div className="h-4 w-24 bg-gray-200 rounded animate-shimmer"
                       style={{ animationDelay: `${index * 100 + 200}ms` }}></div>
                  <div className="h-10 w-full bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg animate-shimmer"
                       style={{ animationDelay: `${index * 100 + 250}ms` }}></div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-end pt-6">
            <div className="w-full sm:w-auto h-12 px-6 bg-gray-200 rounded-lg animate-pulse"></div>
            <div className="w-full sm:w-auto h-12 px-6 bg-gray-200 rounded-lg animate-pulse animation-delay-200"></div>
            <div className="w-full sm:w-auto h-12 px-6 bg-gradient-to-r from-red-300 to-red-300 rounded-lg animate-pulse animation-delay-400"></div>
          </div>
        </div>
      </div>

      {/* Floating elements for visual appeal */}
      <div className="fixed top-20 right-10 w-20 h-20 bg-gradient-to-br from-red-200 to-pink-200 rounded-full blur-xl opacity-50 animate-float"></div>
      <div className="fixed bottom-20 left-10 w-32 h-32 bg-gradient-to-br from-blue-200 to-indigo-200 rounded-full blur-xl opacity-30 animate-float animation-delay-1000"></div>
      <div className="fixed top-40 left-20 w-16 h-16 bg-gradient-to-br from-green-200 to-emerald-200 rounded-full blur-xl opacity-40 animate-float animation-delay-500"></div>
    </div>
  );
};

export default InviteUserFormSkeleton;
